import { userService } from '@/api/user/userApi';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { PasswordInput } from '@/components/ui/password-input';
import { toast } from '@/components/ui/use-toast';
import { cn } from '@/lib/utils';
import {
  changePasswordSchema,
  type ChangePasswordFormData,
} from '@/schemas/changePasswordSchema';
import {
  type ApiError,
  getAxiosErrorStatus,
  handleGlobalError,
} from '@/utils/error-handler';
import { zodResolver } from '@hookform/resolvers/zod';
import { KeyRound, Loader2 } from 'lucide-react';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';

interface ChangePasswordDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Where the reset link goes for accounts that never had a password (Google). */
  email: string;
}

export const ChangePasswordDialog: React.FC<ChangePasswordDialogProps> = ({
  open,
  onOpenChange,
  email,
}) => {
  const [isSendingReset, setIsSendingReset] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    setError,
    getValues,
    trigger,
    formState: { errors, isValid, isSubmitting },
  } = useForm<ChangePasswordFormData>({
    resolver: zodResolver(changePasswordSchema),
    mode: 'onChange',
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
  });

  const isBusy = isSubmitting || isSendingReset;

  // The cross-field rules sit on the field after the one being typed, so
  // editing it would leave their message stale (or missing, with the submit
  // button disabled). Only once that field has something in it, so its own
  // "required" message doesn't show up before the user gets there.
  const revalidateIfFilled = (field: keyof ChangePasswordFormData) => () => {
    if (getValues(field)) void trigger(field);
  };

  const closeAndReset = () => {
    onOpenChange(false);
    reset();
  };

  const onSubmit = async (data: ChangePasswordFormData) => {
    try {
      await userService.changePassword(data.currentPassword, data.newPassword);
      toast({
        title: 'Contraseña actualizada',
        description: 'Tu contraseña se cambió correctamente.',
      });
      closeAndReset();
    } catch (error) {
      // The only 400 this endpoint gives once the form validated is the
      // current password not matching, so it belongs under that field.
      if (getAxiosErrorStatus(error) === 400) {
        const message = (error as { response?: { data?: ApiError } }).response
          ?.data?.message;
        setError('currentPassword', {
          message: message || 'La contraseña actual no es correcta',
        });
        return;
      }
      handleGlobalError(
        error,
        'Error al cambiar la contraseña',
        'No se pudo cambiar la contraseña. Por favor, intentá nuevamente.',
      );
    }
  };

  const sendResetEmail = async () => {
    setIsSendingReset(true);
    try {
      await userService.requestPasswordReset(email);
      toast({
        title: 'Te mandamos un mail',
        description: `Revisá ${email} para crear una contraseña nueva.`,
      });
      closeAndReset();
    } catch (error) {
      handleGlobalError(
        error,
        'Error al enviar el mail',
        'No se pudo enviar el mail para crear una contraseña. Por favor, intentá nuevamente.',
      );
    } finally {
      setIsSendingReset(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // Radix only ever closes here — the trigger lives in the profile page.
        if (!next && !isBusy) closeAndReset();
      }}
    >
      <DialogContent className="sm:max-w-[440px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound size={20} className="text-blue-600" />
            Cambiar contraseña
          </DialogTitle>
          <DialogDescription className="text-left">
            Vas a seguir con la sesión abierta después del cambio.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="currentPassword" className="text-sm font-medium">
              Contraseña actual
            </Label>
            <PasswordInput
              id="currentPassword"
              autoComplete="current-password"
              disabled={isBusy}
              className={cn(errors.currentPassword && 'border-red-500')}
              {...register('currentPassword', {
                onChange: revalidateIfFilled('newPassword'),
              })}
            />
            {errors.currentPassword && (
              <p className="text-sm text-red-500">
                {errors.currentPassword.message}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="newPassword" className="text-sm font-medium">
              Nueva contraseña
            </Label>
            <PasswordInput
              id="newPassword"
              autoComplete="new-password"
              placeholder="Mínimo 6 caracteres"
              disabled={isBusy}
              className={cn(errors.newPassword && 'border-red-500')}
              {...register('newPassword', {
                onChange: revalidateIfFilled('confirmPassword'),
              })}
            />
            {errors.newPassword && (
              <p className="text-sm text-red-500">
                {errors.newPassword.message}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirmPassword" className="text-sm font-medium">
              Repetir nueva contraseña
            </Label>
            <PasswordInput
              id="confirmPassword"
              autoComplete="new-password"
              disabled={isBusy}
              className={cn(errors.confirmPassword && 'border-red-500')}
              {...register('confirmPassword')}
            />
            {errors.confirmPassword && (
              <p className="text-sm text-red-500">
                {errors.confirmPassword.message}
              </p>
            )}
          </div>

          <p className="text-[13px] text-gc-muted">
            ¿Entrás con Google o no recordás tu contraseña actual?{' '}
            <button
              type="button"
              onClick={sendResetEmail}
              disabled={isBusy}
              className="font-medium text-blue-600 hover:underline disabled:cursor-not-allowed disabled:opacity-50"
            >
              Te mandamos un mail para crear una
            </button>
          </p>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={closeAndReset}
              disabled={isBusy}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={!isValid || isBusy}>
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Guardando...
                </>
              ) : (
                'Cambiar contraseña'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
